// Shared response-reading helpers for Supabase Edge Function calls.
//
// Background: prior client code did `throw new Error(await res.text())` or
// `await res.json()` directly. When an upstream proxy (carrier filter,
// iCloud Private Relay, captive portal, MDM, content blocker) intercepts the
// request before it reaches Supabase, the response can be HTML — sometimes
// 50 KB of it — and the old code dumped that whole page into the alert.
//
// Apr 29 2026 incident: Greg's Drone Inspection ASM Review approve button
// rendered an Atlassian-styled "Page not found" page in the alert because
// his connection's proxy substituted the real Supabase response. Edge
// function logs confirmed Supabase never received that POST.
//
// Use these helpers everywhere we hit Supabase Edge Functions:
//   - readEdgeError(res) — returns a friendly string for a !ok response.
//                          Use after `if (!res.ok)`.
//   - readEdgeJson(res)  — parses JSON safely; throws a friendly error for
//                          HTML / text bodies and bad JSON. Use in place of
//                          `await res.json()` so a non-JSON body never
//                          surfaces as a cryptic SyntaxError.
//
// Both helpers return the same friendly message for HTML bodies, so the
// user sees one consistent network-error message no matter which path the
// caller used.

const NETWORK_INTERCEPT_HINT =
  "The request was intercepted before it reached the server — check your connection (try toggling Wi-Fi off/on or switching to cellular) and try again.";

const MAX_USER_MESSAGE_LENGTH = 500;

function looksLikeHtml(s: string): boolean {
  return /^\s*<(?:!doctype|html)/i.test(s);
}

/**
 * Sanitize a string from an untrusted source (e.g., the `error` field of a
 * parsed Edge Function JSON response, or any upstream message that might
 * leak into a UI alert) before showing it to the user. Defense-in-depth:
 *
 * - HTML-shaped strings → friendly network-error message (handles the case
 *   where an Edge Function bubbled a raw upstream HTML body into its JSON
 *   error field).
 * - Strings longer than 500 chars → truncated with ellipsis (handles wall-
 *   of-text errors from misconfigured upstreams).
 * - Empty / nullish → fallback message.
 *
 * The fallback should be a short, action-clear string suitable for an alert
 * (e.g., "Approve failed (HTTP 500)").
 */
export function sanitizeUserMessage(
  s: string | undefined | null,
  fallback: string,
): string {
  if (!s) return fallback;
  if (looksLikeHtml(s)) {
    return `Network error reaching Supabase. ${NETWORK_INTERCEPT_HINT}`;
  }
  if (s.length > MAX_USER_MESSAGE_LENGTH) {
    return s.slice(0, 200) + "… (truncated, full error in console)";
  }
  return s;
}

/**
 * Build a user-readable error message from a non-ok fetch Response.
 * Detects HTML (carrier 404, captive portal, etc.), parses JSON
 * `{ error }` or `{ message }`, and falls back to plain text or the HTTP
 * status. Never reveals raw HTML to the user.
 */
export async function readEdgeError(res: Response): Promise<string> {
  let body = "";
  try {
    body = await res.text();
  } catch {
    return `Network error (HTTP ${res.status})`;
  }
  if (looksLikeHtml(body)) {
    return `Network error reaching Supabase (HTTP ${res.status}). ${NETWORK_INTERCEPT_HINT}`;
  }
  try {
    const j = JSON.parse(body);
    if (typeof j === "object" && j !== null) {
      return j.error || j.message || body || `HTTP ${res.status}`;
    }
    return body || `HTTP ${res.status}`;
  } catch {
    return body || `HTTP ${res.status}`;
  }
}

/**
 * Parse JSON safely from a fetch Response. If the body is HTML or not valid
 * JSON, throws a friendly Error rather than a cryptic SyntaxError. Reads
 * the body via `.text()` first so the same body can be inspected either way.
 *
 * Returns the parsed JSON. Caller should still check `res.ok` and any
 * application-level `body.ok === false` flags.
 *
 * Generic defaults to `unknown` so callers must narrow the type before
 * accessing fields — passes a `T` parameter when the response shape is known
 * (e.g. `readEdgeJson<{ ok?: boolean; error?: string }>(res)`).
 */
export async function readEdgeJson<T = unknown>(res: Response): Promise<T> {
  let body = "";
  try {
    body = await res.text();
  } catch {
    // res.text() can throw on iOS Safari when the response body stream
    // aborts mid-transfer (cellular handoff, Private Relay, etc.). Treat
    // this the same as a fetch network failure.
    throw new Error(
      `Network error reading response (HTTP ${res.status}). ${NETWORK_INTERCEPT_HINT}`,
    );
  }
  if (looksLikeHtml(body)) {
    throw new Error(
      `Network error reaching Supabase (HTTP ${res.status}). ${NETWORK_INTERCEPT_HINT}`,
    );
  }
  try {
    return JSON.parse(body) as T;
  } catch {
    throw new Error(body || `HTTP ${res.status}`);
  }
}

/**
 * Detect a "the request never landed" type of network failure — the kind
 * fetch throws when the connection is intercepted, dropped, or never
 * completes a response. Examples surfaced by this check:
 *   - iOS Safari: `TypeError: Load failed`
 *   - Chrome / Firefox: `TypeError: Failed to fetch`
 *   - Firefox: `TypeError: NetworkError when attempting to fetch resource.`
 *   - aborts: `AbortError`
 *
 * These all share the property that the server may or may not have
 * received the request, and the client received either no response or a
 * partial response. Surfacing these as the bare error name (which is
 * what the unwrapped exception bubbles up as) is hostile to the user;
 * we want the friendly intercept-hint message instead.
 */
function isFetchNetworkFailure(e: unknown): boolean {
  if (!e) return false;
  // DOMException AbortError surfaces as `name === "AbortError"`.
  const name = (e as { name?: string }).name;
  if (name === "AbortError" || name === "TimeoutError") return true;
  // The standard Web Fetch network-error throws are TypeErrors. The
  // message is browser-specific but always mentions fetch / load / network.
  if (e instanceof TypeError) return true;
  const msg = (e as { message?: string }).message ?? "";
  return /load failed|failed to fetch|networkerror|network request failed/i.test(msg);
}

/**
 * fetch() wrapper for Supabase Edge Function calls. Catches the bare
 * network-failure throws from fetch (Safari "Load failed", Chrome
 * "Failed to fetch", abort/timeout, etc.) and rethrows with the
 * NETWORK_INTERCEPT_HINT — same friendly copy we already use when the
 * response body is HTML. Use this everywhere we call edge functions
 * directly so the user sees a consistent, actionable error.
 *
 * Successful responses (including HTTP 4xx/5xx) pass through unchanged
 * — the caller is still responsible for checking `res.ok` and reading
 * the body via `readEdgeJson` / `readEdgeError`.
 */
export async function fetchEdge(input: RequestInfo, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (e) {
    if (isFetchNetworkFailure(e)) {
      throw new Error(`Network error reaching Supabase. ${NETWORK_INTERCEPT_HINT}`);
    }
    throw e;
  }
}

/**
 * Extract the real error message from a `supabase.functions.invoke()` error.
 *
 * When an edge function returns non-2xx, supabase-js resolves with a
 * FunctionsHttpError whose `.message` is always the generic
 * "Edge Function returned a non-2xx status code" — the function's actual
 * JSON error body sits unread in `.context` (the raw Response). Aug 31 2026
 * incident: Hennie's Call button showed that generic string while the server
 * had returned a precise reason for the 502/422. Read the body's
 * `error` / `message` field so the user sees the actual cause.
 *
 * Falls back to the error's own `.message`, then to `fallback`. Everything
 * passes through `sanitizeUserMessage` (HTML bodies, wall-of-text).
 */
export async function invokeErrorMessage(error: unknown, fallback: string): Promise<string> {
  const e = error as { context?: unknown; message?: string } | null;
  const ctx = e?.context;
  if (ctx instanceof Response) {
    try {
      // clone() so a second reader (console logging, retries) still can.
      const body = (await ctx.clone().json()) as { error?: unknown; message?: unknown } | null;
      const msg = body?.error ?? body?.message;
      if (typeof msg === "string" && msg.trim()) return sanitizeUserMessage(msg, fallback);
    } catch {
      // body wasn't JSON — fall through to the generic message
    }
  }
  return sanitizeUserMessage(e?.message, fallback);
}
